import { Link } from 'react-router';
import type { Article, Locale } from '@/types';
import { FramedImage } from '@/components/FramedImage';
import { Markdown } from '@/components/Markdown';
import { ArticleProducts } from '@/components/ArticleProducts';
import { ArticleShare } from '@/components/ArticleShare';
import { GamePromo } from '@/components/GamePromo';
import { EDIT_CATEGORIES } from '@/content/articles';
import { useI18n } from '@/client/i18n/I18nContext';
import { INTL_LOCALE } from '@/lib/seo';
import { EDIT_CATEGORY_SHOP, EDIT_GUIDE_CTA, EDIT_SERVICES_CTA, articlePromoGames } from '@/lib/articles';
import { CARO_PERSON } from '@/config/site';
import { lp } from '@/client/i18n/locale';

/** The Edit category label (translated) or the raw slug for an unknown one. */
export function useEditCategoryLabel(): (category: string) => string {
  const { t } = useI18n();
  return (category) => (EDIT_CATEGORIES.some((c) => c.slug === category) ? t(`edit.cat.${category}`) : category);
}

/**
 * The body of an Edit article (header, text, photo, linked products, store CTA),
 * shared by the public article page and the /admin preview. `article` must
 * already be localized; `locale` formats the date. `share` adds the share row
 * (public page only: a scheduled article's link would be a 404).
 */
export function ArticleView({ article: a, locale, share = false }: { article: Article; locale: Locale; share?: boolean }) {
  const { t } = useI18n();
  const categoryLabel = useEditCategoryLabel();
  const shop = EDIT_CATEGORY_SHOP[a.category];
  const services = a.endLinks?.includes('services') ?? false;
  const guide = a.endLinks?.includes('guide') ?? false;

  return (
    <>
      {/* Text on the left, the article's photo on the right (photo first on phones). */}
      <div className={a.heroImage ? 'media-split' : undefined}>
        <div className="media-split__text">
          <header>
            <p className="eyebrow">{categoryLabel(a.category)}</p>
            <h1 className="section-title">{a.title}</h1>
            <p className="text-muted" style={{ marginTop: 'var(--space-3)' }}>
              {/* Carolina's byline links to her profile (same as the prerender's articleHtml). */}
              {a.author === CARO_PERSON.name ? <Link to={lp(CARO_PERSON.path)}>{a.author}</Link> : a.author} ·{' '}
              {new Date(a.date).toLocaleDateString(INTL_LOCALE[locale])}
            </p>
          </header>
          <div className="prose" style={{ marginTop: 'var(--space-6)' }}>
            <p className="lede">{a.excerpt}</p>
            <Markdown source={a.body} />
          </div>
        </div>
        {a.heroImage ? (
          <div className="media-split__media">
            <FramedImage src={a.heroImage} alt={a.title} fallback="CARO" sizes="(min-width: 900px) 45vw, 100vw" />
          </div>
        ) : null}
      </div>

      <ArticleProducts slugs={a.relatedProductSlugs} />

      {articlePromoGames(a).map((game) => (
        <div key={game} style={{ marginTop: 'var(--space-8)' }}>
          <GamePromo game={game} />
        </div>
      ))}

      {shop || services || guide ? (
        <div
          style={{
            maxWidth: 'var(--width-narrow)',
            marginTop: 'var(--space-8)',
            paddingTop: 'var(--space-6)',
            borderTop: 'var(--border-hairline)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-3)',
          }}
        >
          {shop ? (
            <Link className="btn" to={lp(shop.href)}>
              {t(shop.key)}
            </Link>
          ) : null}
          {services ? (
            <Link className="btn btn--ghost" to={lp(EDIT_SERVICES_CTA.href)}>
              {t(EDIT_SERVICES_CTA.key)}
            </Link>
          ) : null}
          {guide ? (
            <Link className="btn btn--ghost" to={lp(EDIT_GUIDE_CTA.href)}>
              {t(EDIT_GUIDE_CTA.key)}
            </Link>
          ) : null}
        </div>
      ) : null}

      {share ? <ArticleShare slug={a.slug} title={a.title} locale={locale} /> : null}
    </>
  );
}

/** Edit card (photo 4:5, category, title, excerpt) linking to the article. */
export function EditorialCard({
  article: a,
  sizes = '(min-width: 768px) 33vw, 100vw',
}: {
  article: Article;
  /** Rendered width hint for the srcset (the /the-edit grid is denser than home / PDP). */
  sizes?: string;
}) {
  const categoryLabel = useEditCategoryLabel();
  return (
    <Link to={lp(`/the-edit/post/${a.slug}`)} className="editorial-card">
      <div className="editorial-card__media">
        {a.heroImage ? (
          <FramedImage src={a.heroImage} alt={a.title} loading="lazy" fallback="CARO" sizes={sizes} />
        ) : null}
      </div>
      <p className="eyebrow editorial-card__cat">{categoryLabel(a.category)}</p>
      <h3>{a.title}</h3>
      <p className="text-muted">{a.excerpt}</p>
    </Link>
  );
}
